<template>
  <div class="contacten-filter container" ref="filter-root">
    <div class="top-row row p-3 bg-white border rounded">
      <div class="filter-block col-12 col-md-4">
        <h2>Filter contacten</h2>

        <ContactSearch :selectValues="gemeenten" />

        <template v-if="queryResult">
          <div v-if="queryResult">
            <div class="mb-1 mr-3 d-flex">
              <span>{{ queryResult.length }} gevonden contacten</span>
            </div>
          </div>
          <div v-if="!queryResult">
            <div class="mt-3 d-flex justify-content-center">Geen items gevonden</div>
          </div>
        </template>

        <div class="card-deck d-flex flex-column">
          <a
            :key="item.id"
            :ref="'contact-' + item.id"
            v-for="item in queryResult"
            v-on:click="markerSelected(item)"
          >
            <ContactCard :contact="item.properties" :selected="item === selectedMarker" />
          </a>
        </div>
      </div>

      <div class="map-block col-12 col-md-8">
        <GoogleMapLoader :mapConfig="mapConfig" :apiKey="googleMapsKey" v-slot="{ google, map }">
          <GoogleMapGeoJson
            :google="google"
            :map="map"
            :geojson="geojson_provincie"
            :showFromZoom="0"
          />
          <GoogleMapGeoJson
            :google="google"
            :map="map"
            :geojson="geojson_gemeenten"
            :showFromZoom="9"
            :geoJsonStyle="geojsonGemeenteStyle"
            :selectedFeatureCode="administrativeDivision"
            :selectedFeatureFillColor="'red'"
            @featureSelected="gemeenteSelected"
          />
          <GoogleMapInfoWindow
            :google="google"
            :map="map"
            :selectedMarker="selectedMarker"
            v-on:closed="clearMarker"
          />
          <template v-if="queryResult">
            <GoogleMapMarkerCluster
              :google="google"
              :map="map"
              imagePath="https://developers.google.com/maps/documentation/javascript/examples/markerclusterer/m"
              v-slot="{ markerCluster }"
            >
              <GoogleMapSpiderfier :google="google" :map="map" v-slot="{ spiderfier }">
                <GoogleMapMarker
                  v-for="item in queryResult"
                  :key="item.id"
                  :marker="item"
                  :google="google"
                  :map="map"
                  :markerCluster="markerCluster"
                  :spiderfier="spiderfier"
                  :icon="icon"
                  v-on:marker-selected="markerSelected"
                />
              </GoogleMapSpiderfier>
            </GoogleMapMarkerCluster>
          </template>
        </GoogleMapLoader>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import throttle from 'lodash.throttle';
import lazySizes from 'lazysizes'; // pikt juiste breedte voor img srcset
import 'lazysizes/plugins/attrchange/ls.attrchange'; // nodig na filtering
import {
  mapGetters, mapState, mapMutations, mapActions,
} from 'vuex';

import mapSettings from '@/constants/mapSettings';
import store from '../store';
import ContactCard from './ContactCard.vue';
import ContactSearch from './ContactSearch.vue';
import GoogleMapLoader from './GoogleMapLoader.vue';
import GoogleMapMarker from './GoogleMapMarker.vue';
import GoogleMapMarkerCluster from './GoogleMapMarkerCluster.vue';
import GoogleMapSpiderfier from './GoogleMapSpiderfier.vue';
import GoogleMapGeoJson from './GoogleMapGeoJson.vue';
import GoogleMapInfoWindow from './GoogleMapInfoWindow.vue';

lazySizes.cfg.loadMode = 0; // no eager loading of images

const contactMarker = require('@/assets/contact_marker.png');

export default {
  name: 'ContactFilter',
  store,
  components: {
    ContactCard,
    ContactSearch,
    GoogleMapLoader,
    GoogleMapMarker,
    GoogleMapMarkerCluster,
    GoogleMapSpiderfier,
    GoogleMapGeoJson,
    GoogleMapInfoWindow,
  },
  data() {
    return {
      baseUrl: '',
      queryResult: null,
      loading: false,
      cancelToken: null,
      geojsonGemeenteStyle: {
        fillColor: 'black',
        strokeWeight: 1,
      },
    };
  },
  props: {
    latitude: Number,
    longitude: Number,
    zoom: Number,
  },
  computed: {
    ...mapState({
      selectedMarker: state => state.map.marker,
      administrativeDivision: state => state.map.administrativeDivision,
      searchQuery: state => state.map.searchQuery,
      geojson_provincie: state => state.geojson.drentheShape,
      geojson_gemeenten: state => state.geojson.gemeentenShape,
    }),
    ...mapGetters('geojson', {
      gemeenten: 'getGemeentenValues',
    }),
    googleMapsKey() {
      return `${process.env.VUE_APP_GOOGLE_MAPS_KEY}`;
    },
    icon() {
      return { url: contactMarker };
    },
    mapConfig() {
      return {
        ...mapSettings,
        center: this.mapCenter,
        zoom: this.zoom,
      };
    },

    mapCenter() {
      return { lat: this.latitude, lng: this.longitude };
    },
  },
  mounted() {
    this.queryContacts();
  },
  created() {
    // just to make sure that we limit the number of calls to our backend a little
    this.queryContacts = throttle(this.queryContacts, 500);
  },
  destroyed() {},
  methods: {
    ...mapMutations({
      markerSelected: 'map/setMarker',
      gemeenteSelected: 'map/setAdministrativeDivision',
      searchQueryInput: 'map/searchQuery',
    }),
    ...mapActions({
      clearMarker: 'map/clearMarker',
      gemeenteClear: 'map/clearAdministrativeDivision',
    }),
    showItem(item) {
      window.location.href = item.url;
    },
    getQueryParams() {
      const params = new URLSearchParams();

      if (this.administrativeDivision) {
        params.append('division_code', this.administrativeDivision);
      }
      if (this.searchQuery) {
        params.append('search', this.searchQuery);
      }

      return params;
    },
    queryContacts($state) {
      if (this.cancelToken) {
        // cancel ongoing request
        this.cancelToken.cancel('Only one request allowed at a time.');
      }
      this.cancelToken = axios.CancelToken.source();

      return this.doQueryContacts(this.getQueryParams(), $state);
    },
    doQueryContacts(params) {
      this.loading = true;
      const contactenUrl = `${process.env.VUE_APP_API_URL}contacts/`;

      const qq = async () => {
        const promise = axios.get(contactenUrl, {
          params,
          cancelToken: this.cancelToken.token,
        });
        await promise
          .then((response) => {
            this.queryResult = response.data.features;
            this.loading = false;
          })
          .catch((error) => {
            // error callback
            if (axios.isCancel(error)) {
              // no worries
            } else {
              // handle error
              this.loading = false;
              throw error.message;
            }
          })
          .then(() => {});
      };
      qq();
    },
    doFilter() {
      this.queryContacts();
    },
  },
  watch: {
    administrativeDivision() {
      this.doFilter();
    },
    searchQuery() {
      this.doFilter();
    },
    selectedMarker() {
      if (!this.selectedMarker) {
        return;
      }
      const markerReference = this.$refs[`contact-${this.selectedMarker.id}`];
      if (markerReference.length > 0) {
        markerReference[0].scrollIntoView(true);
        window.scrollBy(0, -250);
      }
    },
  },
};
</script>

<style lang="scss">
.render-breaks {
  white-space: pre;
}
.card-deck {
  max-height: 560px;
  overflow-y: scroll;
  flex-flow: column nowrap !important;
}
.map-block {
  height: 600px;
}
article.card,
#content article.card {
  box-shadow: none;
  border: 1px solid rgba(0, 0, 0, 0.125);
  border-radius: 0.25rem;
  margin-bottom: 0.25em;
}
article.card.selected,
#content article.card.selected {
  border-color: black;
}
.filter-block select {
  background: none;
}
</style>
